import { useQuery } from "@tanstack/react-query";
import { productService, ProductResponse } from "../services/productService";
import { useProductStore } from "../store/productStore";
import { Product, ProductFilters } from "../models/product";
import { useMemo } from "react";

export const useProducts = () => {
  const filters = useProductStore((state) => state.filters);

  const { data, isLoading, isError } = useQuery<ProductResponse>({
    queryKey: ["products"],
    queryFn: () => productService.getProducts(),
  });

  const filteredProducts = useMemo(() => {
    if (!data?.data?.products) return [];

    let products = [...data.data.products];

    // Apply search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      products = products.filter(
        product =>
          product.name.toLowerCase().includes(searchTerm) ||
          product.description.toLowerCase().includes(searchTerm) ||
          product.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    }

    // Apply price range filter
    if (filters.minPrice !== undefined) {
      products = products.filter(product => product.price >= filters.minPrice!);
    }
    if (filters.maxPrice !== undefined) {
      products = products.filter(product => product.price <= filters.maxPrice!);
    }

    // Apply tags filter
    if (filters.tags && filters.tags.length > 0) {
      products = products.filter(product =>
        filters.tags!.some(tag => product.tags.includes(tag))
      );
    }

    // Apply sort
    if (filters.sort) {
      switch (filters.sort) {
        case 'highest':
          products.sort((a, b) => b.price - a.price);
          break;
        case 'lowest':
          products.sort((a, b) => a.price - b.price);
          break;
        case 'latest':
          products.sort((a, b) => b.id.localeCompare(a.id));
          break;
      }
    }

    return products;
  }, [data?.data?.products, filters]);

  return {
    data: {
      data: {
        products: filteredProducts,
        allProducts: data?.data?.products || [],
        onSaleProducts: data?.data?.onSaleProducts || []
      }
    },
    isLoading,
    isError
  };
};

export const useProduct = (id: string) => {
  const setSelectedProduct = useProductStore((state) => state.setSelectedProduct);

  const query = useQuery<Product>({
    queryKey: ["product", id],
    queryFn: () => productService.getProducts(1, 1, id).then(res => res.data.products[0]),
  });

  if (query.data) {
    setSelectedProduct(query.data);
  }

  return query;
}; 